// src/app/components/admin/wind-tunnel/wind-tunnel-shared.tsx
//
// Presentational pieces shared by the Wind tunnel form and report. They
// follow the admin portal's template (Global Settings → Health Details): 12px
// cards with an icon + title row, pill status chips, the coral error note,
// the kit Button, and the shared ui/ Input and Textarea.

import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { AlertCircle } from "lucide-react";

import { Button as KitButton } from "../../kit";
import { Input as UiInput } from "../../ui/input";
import { Textarea as UiTextarea } from "../../ui/textarea";
import type {
  GateStatus, ShadeBand, WindTunnelDraftVerdict, WindTunnelStatus, WindTunnelVerdict,
} from "@/api/wind-tunnel";

export const labelStyle: CSSProperties = {
  fontSize: "var(--text-sm)",
  fontWeight: "var(--font-weight-medium)",
  color: "var(--foreground)",
};

export const helpStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--muted-foreground)",
  lineHeight: 1.5,
};

// ui/Input and ui/Textarea draw their edge with --input, which this theme sets
// to pure white — on a white card the field has no visible border at all.
// These wrappers give every field the card's --border edge and a teal focus.
const FIELD_EDGE =
  "border-[var(--border)] bg-[var(--background)] focus-visible:border-[var(--alpha-teal)] focus-visible:ring-[var(--alpha-teal)]/20";

export function Input({ className = "", ...props }: ComponentProps<typeof UiInput>) {
  return <UiInput className={`${FIELD_EDGE} ${className}`} {...props} />;
}

export function Textarea({ className = "", ...props }: ComponentProps<typeof UiTextarea>) {
  return <UiTextarea className={`${FIELD_EDGE} ${className}`} {...props} />;
}

/** Native <select>, styled like the fields above. */
export const selectClass =
  "border-[var(--border)] bg-[var(--background)] focus-visible:border-[var(--alpha-teal)] focus-visible:ring-[var(--alpha-teal)]/20 flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base md:text-sm " +
  "outline-none transition-[color,box-shadow] focus-visible:ring-[3px] " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const cardStyle: CSSProperties = {
  backgroundColor: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  padding: 20,
};

/** The admin card: icon + title row, optional hint and right-hand slot. */
export function Section({
  title, icon, hint, children, aside,
}: {
  title: string;
  icon?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section style={cardStyle}>
      <div className="flex flex-wrap items-start justify-between gap-3" style={{ marginBottom: 14 }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2" style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
            {icon}
            {title}
          </div>
          {hint && <p style={{ ...helpStyle, marginTop: 4 }}>{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Page heading inside the content column (same as Health Details / Logs). */
export function PageHeading({
  icon, title, subtitle, right,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {icon}
          <h2
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
              fontWeight: 700,
              margin: 0,
              lineHeight: 1.3,
              color: "var(--foreground)",
            }}
          >
            {title}
          </h2>
        </div>
        {subtitle && <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted-foreground)" }}>{subtitle}</div>}
      </div>
      {right && <div className="flex flex-wrap gap-2 sm:justify-end">{right}</div>}
    </div>
  );
}

export function Button({
  children, onClick, disabled, variant = "secondary", type = "button", title,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger";
  type?: "button" | "submit";
  title?: string;
}) {
  return (
    <KitButton
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      variant={variant === "danger" ? "destructive" : variant}
    >
      {children}
    </KitButton>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div
      className="p-3 flex items-start gap-2 rounded-lg"
      style={{ backgroundColor: "var(--infrared-coral-5)", border: "1px solid var(--infrared-coral)", fontSize: 13, color: "var(--foreground)" }}
    >
      <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)", flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

const TONE: Record<string, string> = {
  PASS: "var(--signal-green)",
  OK: "var(--signal-green)",
  CONDITIONAL: "var(--impact-medium, #d97706)",
  WARN: "var(--impact-medium, #d97706)",
  FAIL: "var(--infrared-coral)",
};

/** Status pill — same shape as Health Details' StatusPill. */
export function VerdictChip({
  verdict, provisional, size = "sm",
}: {
  verdict: WindTunnelVerdict | GateStatus | null;
  provisional?: boolean;
  size?: "sm" | "lg";
}) {
  const color = verdict ? TONE[verdict] ?? TONE.CONDITIONAL : "var(--muted-foreground)";
  return (
    <span
      className="inline-flex items-center gap-1"
      title={provisional ? "Provisional — the run did not finish its full battery" : undefined}
      style={{
        padding: size === "lg" ? "4px 14px" : "2px 9px",
        borderRadius: 999,
        fontSize: size === "lg" ? 14 : 11.5,
        fontWeight: 600,
        color,
        border: `1px ${provisional ? "dashed" : "solid"} ${color}`,
        whiteSpace: "nowrap",
      }}
    >
      {verdict ?? "—"}
      {verdict && provisional && <span style={{ fontWeight: 500 }}>· provisional</span>}
    </span>
  );
}

/** One small letter per draft (P / C / F) for the narrow run list; the
 * draft's label and full verdict are in the tooltip. */
export function DraftVerdictMarks({
  verdicts, provisional,
}: {
  verdicts: WindTunnelDraftVerdict[];
  provisional?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1" aria-label="Verdict per draft">
      {verdicts.map((draft) => {
        const color = draft.verdict ? TONE[draft.verdict] : "var(--muted-foreground)";
        return (
          <span
            key={draft.id}
            title={`${draft.label}: ${draft.verdict ?? "no verdict"}${provisional && draft.verdict ? " (provisional)" : ""}`}
            className="inline-flex items-center justify-center"
            style={{
              width: 18, height: 18, borderRadius: 999, fontSize: 10.5, fontWeight: 700, color,
              border: `1px ${provisional ? "dashed" : "solid"} ${color}`,
            }}
          >
            {draft.verdict ? draft.verdict[0] : "—"}
          </span>
        );
      })}
    </span>
  );
}

/** Cell tint for a difference from control: unshaded inside the warn line,
 * light past it, strong past the fail line. Green for a gain, coral for a loss. */
export const SHADE_BG: Record<NonNullable<ShadeBand>, string> = {
  gain: "var(--signal-green-10)",
  gain_strong: "var(--signal-green-20)",
  loss: "var(--infrared-coral-10)",
  loss_strong: "var(--infrared-coral-20)",
};

const STATUS_LABEL: Record<WindTunnelStatus, string> = {
  pending: "Queued",
  running: "Running",
  completed: "Completed",
  cancelled: "Stopped",
  failed: "Failed",
};

export function StatusText({ status, stopReason, stale }: { status: WindTunnelStatus; stopReason?: string | null; stale?: boolean }) {
  const color =
    status === "failed" ? "var(--infrared-coral)"
      : status === "running" || status === "pending" ? "var(--signal-blue)"
        : "var(--muted-foreground)";
  const reason = stopReason === "spend_cap" ? "spend cap reached" : stopReason === "cancelled" ? "cancelled" : null;
  return (
    <span style={{ fontSize: 12, color }}>
      {STATUS_LABEL[status] ?? status}
      {reason ? ` · ${reason}` : ""}
      {stale ? " · stalled" : ""}
    </span>
  );
}

export function pct(rate: number | null | undefined, digits = 1): string {
  return rate === null || rate === undefined ? "—" : `${(rate * 100).toFixed(digits)}%`;
}

export function num(value: number | null | undefined, digits = 3, signed = false): string {
  if (value === null || value === undefined) return "—";
  const text = value.toFixed(digits);
  return signed && value > 0 ? `+${text}` : text;
}

export function usd(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `$${value.toFixed(2)}`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
